import { useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import Card from "../../components/Card";
import {
  EmptyState,
  ErrorBanner,
  ErrorState,
  skeletonTableProps,
} from "../../components/states";
import { useToast } from "../../components/Toast/toastHook";
import { GET_ALL_SERVICES } from "../../graphql/agentQueries";
import { Table, Button } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import { formatDate } from "../../utils/dateFormat";
import TableToolbar from "../../components/TableToolbar/TableToolbar";
import { toServerSort } from "../../utils/tableSort";
import { TABLE_SCROLL } from "../../constants/table";

// Columns the server can order the services by.
const SORTABLE_COLUMNS = ["title", "createdAt"];

const AgentServices = () => {
  const [services, setServices] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({ page: 1, limit: 10 });
  const [totalCount, setTotalCount] = useState(0);
  const [sort, setSort] = useState(null);
  const { companyId } = useParams();
  const navigate = useNavigate();
  const toast = useToast(4000);

  const { loading, error, data, refetch } = useQuery(GET_ALL_SERVICES, {
    variables: {
      companyId,
      pagination,
      searchText: searchText || undefined,
      ...(sort && { sort }),
    },
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (data?.getAllServices) {
      setServices(data.getAllServices.services || []);
      setTotalCount(data.getAllServices.totalCount || 0);
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      toast("error", error.message);
    }
  }, [error]);

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination({ ...pagination, page: 1 });
  };

  const handleTableChange = (paginationConfig, _tableFilters, sorter, extra) => {
    // A new sort order starts again from the first page.
    if (extra?.action === "sort") {
      setSort(toServerSort(sorter, SORTABLE_COLUMNS));
      setPagination((prev) => ({ ...prev, page: 1 }));
      return;
    }
    setPagination({
      page: paginationConfig.current,
      limit: paginationConfig.pageSize,
    });
  };

  const handleViewSubCompanies = (service) => {
    navigate(`/company/${companyId}/agent/services/${service._id}/sub-companies`, {
      state: { serviceName: service.title },
    });
  };

  const columns = [
    {
      title: "Icon",
      dataIndex: "icon",
      key: "icon",
      width: 80,
      render: (icon) =>
        icon ? (
          <img
            src={icon}
            alt="Service Icon"
            className="w-10 h-10 object-contain"
          />
        ) : (
          <div className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center">
            <span className="text-gray-400 text-xs">N/A</span>
          </div>
        ),
    },
    {
      title: "Service Name",
      dataIndex: "title",
      key: "title",
      sorter: true,
      render: (title) => <span className="font-medium text-black">{title}</span>,
    },
    {
      title: "Created Date",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: true,
      render: (date) => formatDate(date) || "-",
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Button
          icon={<EyeOutlined />}
          onClick={() => handleViewSubCompanies(record)}
          title="View Sub Companies"
        >
          View
        </Button>
      ),
    },
  ];

  const firstLoad = loading && services.length === 0;

  return (
    <div id="agent-services">
      <Breadcrumb pageName="Services" />
      <div className="pb-6">
        <Card>
          <TableToolbar
            title="Services"
            count={totalCount}
            searchValue={searchText}
            onSearch={handleSearch}
            searchPlaceholder="Search services..."
          />
          {error && !services.length ? (
            <ErrorState onRetry={() => refetch()} />
          ) : (
            <>
              {error && <ErrorBanner onRetry={() => refetch()} />}
              <Table
                scroll={TABLE_SCROLL}
                bordered
                rowKey="_id"
                onChange={handleTableChange}
                {...(firstLoad
                  ? skeletonTableProps(columns)
                  : {
                      columns,
                      dataSource: services,
                      loading,
                      pagination: {
                        position: ["bottomCenter"],
                        current: pagination.page,
                        pageSize: pagination.limit,
                        total: totalCount,
                        showSizeChanger: true,
                      },
                    })}
                locale={{
                  emptyText: searchText ? (
                    <EmptyState
                      title="No services match your search"
                      description={`Nothing found for "${searchText}".`}
                      actionLabel="Clear search"
                      onAction={() => setSearchText("")}
                    />
                  ) : (
                    <EmptyState
                      title="No services available"
                      description="Services offered to your company will appear here."
                    />
                  ),
                }}
              />
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default AgentServices;
